import * as React from 'react';
import { cn } from '@/shared/utils/styling';
import { tv } from 'tailwind-variants';

interface CardProps extends React.ComponentProps<'div'> {
  variant?: 'default' | 'details' | 'no-effects';
}

const cardVariants = tv({
  base: 'flex flex-col',
  variants: {
    variant: {
      default:
        'bg-card text-card-foreground gap-6 rounded-[2.4rem] py-6 shadow-sm',
      details: 'rounded-[1.6rem] px-[2.4rem] py-[1.6rem] gap-[1.6rem] bg-muted',
      'no-effects': 'border-none shadow-none rounded-[2.4rem] bg-card',
    },
  },
});

function Card({ className, variant, ...props }: CardProps) {
  const baseClass = cardVariants({ variant: variant || 'default' });

  return (
    <div data-slot="card" className={cn(baseClass, className)} {...props} />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-secondary p3', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-6', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
